import { searchOverview } from '@api/search/searchQueries';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { captureException } from '@sentry/nextjs';
import { request } from '@utils/graphql-request';
import { swrTtlMiddleware } from '@repo/ui/src/utils/swrTtlMiddleware';
import useSWRImmutable from 'swr/immutable';

import { isInGeo } from './useSearch';

export interface OverviewSection {
  nbHits: number;
  results: Array<any>;
}

export type SearchOverview = Record<string, OverviewSection>;

interface Key {
  searchTerm: string;
  locale: string;
  productGeo?: string;
  pageSize: number;
}

/**
 * Fetches the first page of hits and the total hit count for every search type in a single request.
 * @param {Key} key - SWR key.
 * @param {string} key.searchTerm - Term to search for.
 * @param {string} key.locale - Locale used for the search.
 * @param {string} [key.productGeo] - Geo used to filter product hits.
 * @param {number} key.pageSize - Number of hits to return per type.
 * @returns Map of search type to its hits and total hit count.
 */
const fetcher = async ({
  searchTerm,
  locale,
  productGeo,
  pageSize,
}: Key): Promise<SearchOverview> => {
  const response = await request<Record<string, { hits?: Array<any>; nbHits?: number } | null>>({
    document: searchOverview,
    variables: { query: searchTerm, pageSize, page: 0 },
    requestHeaders: { 'Accept-Language': locale || 'en-US' },
    locale,
  });
  return Object.fromEntries(
    Object.entries(response ?? {}).map(([type, section]) => [
      type,
      {
        nbHits: section?.nbHits ?? 0,
        results: (section?.hits ?? []).filter((item) => isInGeo(item, productGeo)),
      },
    ]),
  );
};

/**
 * Returns hits and hit counts for every search type from a single request.
 * @param {string} searchTerm - Term to search for; an empty term disables the request.
 * @param {number} pageSize - Number of hits to return per type.
 * @returns Overview per search type plus SWR loading state; overview is empty until loaded.
 */
export const useSearchOverview = (searchTerm: string, pageSize: number) => {
  const { locale = 'en-US', productGeo } = useCurrentUrl();

  const { data, isLoading, isValidating } = useSWRImmutable(
    searchTerm ? { searchTerm, locale, productGeo, pageSize } : null,
    fetcher,
    {
      keepPreviousData: true,
      use: [swrTtlMiddleware()],
      onError: (error) => captureException(error),
    },
  );

  return { overview: data ?? {}, isLoading, isValidating };
};
